import React, { useState } from 'react';
import { ChatBubbleOvalLeftIcon } from '@heroicons/react/24/outline';
import NoteModal from '../../../modals/NoteModal';
import { useDispatch } from 'react-redux';
import { createNote, updateNote } from '../../../store/thunks/notesThunk';

function ResourceInfo({ resource, dailyNote, calendarDate, onNoteUpdate, onNoteDelete }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noteText, setNoteText] = useState(dailyNote);

  const getBgColor = () => {
    // Safely check if resource and specialization exist
    if (!resource) return "bg-gray-100";
    
    // Handle resources that might not have specialization property
    if (!resource.specialization || resource.specialization.length === 0) {
      // Use type instead if available, otherwise default to gray
      if (resource.type) {
        switch (resource.type) {
          case "Room":
            return "bg-blue-100";
          case "Equipment":
            return "bg-green-100";
          case "Testing Material":
            return "bg-yellow-100";
          case "Assistive Device":
            return "bg-purple-100";
          default:
            return "bg-gray-100";
        }
      }
      return "bg-gray-100";
    }
    
    // Original logic for resources with specialization
    switch (resource.specialization[0]) {
      case "SLP":
        return "bg-blue-100";
      case "CDA":
        return "bg-green-100";
      default:
        return "bg-gray-100";
    }
  };

  const handleNoteClick = () => {
    setNoteText(dailyNote); // Set the current note text
    setIsModalOpen(true); // Open the modal
  };

  // Handle missing firstName/lastName properties
  const getResourceName = () => {
    if (!resource) return "Resource";
    
    if (resource.firstName && resource.lastName) {
      return `${resource.firstName} ${resource.lastName}`;
    } else if (resource.name) {
      return resource.name;
    } else {
      return "Resource";
    }
  };

  // Handle missing qualifications/credentials properties
  const getResourceType = () => {
    if (!resource) return "Resource";
    
    if (resource.qualifications) {
      return resource.qualifications;
    } else if (resource.credentials) {
      return resource.credentials;
    } else if (resource.type) {
      return resource.type;
    } else {
      return "Resource";
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center w-full p-2 border-b border-gray-200 border-l-2 ${getBgColor()}`}>
      <h2 className="text-lg font-semibold">{getResourceName()}</h2>
      <p className="text-sm text-gray-500">{getResourceType()}</p>

      {/* Speech Bubble */}
      <div 
        className="absolute top-2 right-2 cursor-pointer z-100"
        onClick={handleNoteClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <ChatBubbleOvalLeftIcon 
          className={`h-6 w-6 ${dailyNote ? 'text-white' : 'text-gray-600'}`} 
          style={{
            fill: dailyNote ? 'rgb(239, 68, 68)' : 'transparent',
            stroke: dailyNote ? 'rgb(239, 68, 68)' : 'currentColor'
          }}
        />
      </div>

      {/* Dialogue Box */}
      {isHovered && dailyNote && (
        <div className="absolute top-10 right-2 bg-white border border-gray-300 rounded shadow-md p-2">
          <pre className="text-sm text-gray-800 whitespace-pre-wrap">{dailyNote.content}</pre>
        </div>
      )}

      {/* Note Modal */}
      {isModalOpen && (
        <NoteModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          dailyNote={dailyNote}
          onSave={onNoteUpdate}
          onDelete={onNoteDelete}
          resource={resource}
          calendarDate={calendarDate}
        />
      )}
    </div>
  );
}

export default ResourceInfo